import { Heading, Label, Select, Surface, Text, TextInput } from '@elladx/design-system';
import type { SenderAddress } from '../../types';
import type { SelectOption } from '../../constants/regions';
interface SenderAddressSectionProps {
    address: SenderAddress;
    regions: SelectOption[];
    onChange: (field: keyof SenderAddress, value: string) => void;
    errors?: Partial<Record<keyof SenderAddress, string>>;
}
function SenderAddressSection({ address, regions, onChange, errors = {} }: SenderAddressSectionProps) {
    return (<Surface className="form-section">
      <Heading as="h2" variant="card">Sender Address</Heading>
      <Text className="section-description">
        This address will be used as the default sender (from) address for all shipments.
        EasyPost validates addresses against carrier databases.
      </Text>

      <div className="form-grid-settings">
        <div className="form-group">
          <Label htmlFor="sender-name">Name *</Label>
          <TextInput type="text" id="sender-name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'sender-name-error' : undefined} value={address.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Full name or business name"/>
          {errors.name && <Text id="sender-name-error" className="field-error">{errors.name}</Text>}
        </div>

        <div className="form-group">
          <Label htmlFor="sender-company">Company</Label>
          <TextInput type="text" id="sender-company" value={address.company} onChange={(e) => onChange('company', e.target.value)} placeholder="Company name (optional)"/>
        </div>

        <div className="form-group full-width">
          <Label htmlFor="sender-street1">Street Address *</Label>
          <TextInput type="text" id="sender-street1" aria-invalid={Boolean(errors.street1)} aria-describedby={errors.street1 ? 'sender-street1-error' : undefined} value={address.street1} onChange={(e) => onChange('street1', e.target.value)} placeholder="Street address"/>
          {errors.street1 && <Text id="sender-street1-error" className="field-error">{errors.street1}</Text>}
        </div>

        <div className="form-group full-width">
          <Label htmlFor="sender-street2">Address Line 2</Label>
          <TextInput type="text" id="sender-street2" value={address.street2} onChange={(e) => onChange('street2', e.target.value)} placeholder="Apt, suite, unit, etc. (optional)"/>
        </div>

        <div className="form-group">
          <Label htmlFor="sender-city">City *</Label>
          <TextInput type="text" id="sender-city" aria-invalid={Boolean(errors.city)} aria-describedby={errors.city ? 'sender-city-error' : undefined} value={address.city} onChange={(e) => onChange('city', e.target.value)} placeholder="City"/>
          {errors.city && <Text id="sender-city-error" className="field-error">{errors.city}</Text>}
        </div>

        <div className="form-group">
          <Label htmlFor="sender-state">{address.country === 'CA' ? 'Province' : 'State'} *</Label>
          <Select id="sender-state" aria-invalid={Boolean(errors.state)} aria-describedby={errors.state ? 'sender-state-error' : undefined} value={address.state} onChange={(e) => onChange('state', e.target.value)}>
            {regions.map((state) => (<option key={state.value} value={state.value}>
                {state.label}
              </option>))}
          </Select>
          {errors.state && <Text id="sender-state-error" className="field-error">{errors.state}</Text>}
        </div>

        <div className="form-group">
          <Label htmlFor="sender-zip">{address.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</Label>
          <TextInput type="text" id="sender-zip" aria-invalid={Boolean(errors.zip)} aria-describedby={errors.zip ? 'sender-zip-error' : undefined} value={address.zip} onChange={(e) => onChange('zip', e.target.value)} placeholder="ZIP code" maxLength={10}/>
          {errors.zip && <Text id="sender-zip-error" className="field-error">{errors.zip}</Text>}
        </div>

        <div className="form-group">
          <Label htmlFor="sender-country">Country</Label>
          <Select id="sender-country" value={address.country} onChange={(e) => onChange('country', e.target.value)}>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </Select>
        </div>

        <div className="form-group">
          <Label htmlFor="sender-phone">Phone</Label>
          <TextInput type="tel" id="sender-phone" value={address.phone} onChange={(e) => onChange('phone', e.target.value)} placeholder="(555) 555-5555"/>
        </div>

        <div className="form-group">
          <Label htmlFor="sender-email">Email</Label>
          <TextInput type="email" id="sender-email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'sender-email-error' : undefined} value={address.email} onChange={(e) => onChange('email', e.target.value)} placeholder="email@example.com"/>
          {errors.email && <Text id="sender-email-error" className="field-error">{errors.email}</Text>}
        </div>
      </div>
    </Surface>);
}
export default SenderAddressSection;
